import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import clsx from 'clsx';
import { ACCOUNT_BILLING_CREDITS_PATH } from '@/routes/paths';

const VOICE_CLONE_COST = 10;
const PREMIUM_SCRAPING_COST = 55;

interface TrainingConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  hasVoiceClone: boolean;
  hasPremiumScraping: boolean;
  hasFreeScraping: boolean;
  fileCount: number;
  linkCount: number;
  userCredits: number;
}

interface ChecklistItem {
  label: string;
  detail?: string;
  present: boolean;
  cost?: number;
}

export const TrainingConfirmModal: React.FC<TrainingConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  hasVoiceClone,
  hasPremiumScraping,
  hasFreeScraping,
  fileCount,
  linkCount,
  userCredits,
}) => {
  const navigate = useNavigate();

  const totalCost =
    (hasVoiceClone ? VOICE_CLONE_COST : 0) + (hasPremiumScraping ? PREMIUM_SCRAPING_COST : 0);
  const hasEnoughCredits = userCredits >= totalCost;
  const missingCredits = totalCost - userCredits;

  const checklist: ChecklistItem[] = [
    {
      label: 'Training files',
      detail:
        fileCount > 0 ? `${fileCount} file${fileCount > 1 ? 's' : ''} ready` : 'No files uploaded',
      present: fileCount > 0,
    },
    {
      label: 'Links',
      detail:
        linkCount > 0 ? `${linkCount} link${linkCount > 1 ? 's' : ''} to scrape` : 'No links added',
      present: linkCount > 0,
    },
    {
      label: 'Internet search',
      detail: hasPremiumScraping
        ? 'Premium — deep search across multiple sources'
        : hasFreeScraping
          ? 'Free — basic public info from the web'
          : 'Not activated',
      present: hasFreeScraping || hasPremiumScraping,
      ...(hasPremiumScraping ? { cost: PREMIUM_SCRAPING_COST } : {}),
    },
    {
      label: 'Voice clone',
      detail: hasVoiceClone ? 'Audio sample saved' : 'No voice recorded',
      present: hasVoiceClone,
      ...(hasVoiceClone ? { cost: VOICE_CLONE_COST } : {}),
    },
  ];

  const presentCount = checklist.filter((item) => item.present).length;

  const handleBuyCredits = () => {
    navigate(ACCOUNT_BILLING_CREDITS_PATH);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className='max-w-screen-xs'>
      <div className='flex flex-col'>
        {hasEnoughCredits ? (
          <>
            <h2 className='mb-1 font-sans text-24 font-bold leading-tight text-dark-300'>
              Ready to Train
            </h2>
            <p className='mb-6 font-sans text-14 font-normal leading-20 text-gray-530'>
              The more content you provide, the better your echo will understand and replicate your
              personality.
            </p>

            {/* Checklist */}
            <div className='mb-6 flex flex-col gap-3'>
              {checklist.map((item) => (
                <div
                  key={item.label}
                  className={clsx(
                    'flex items-start gap-3 rounded-12 px-4 py-3',
                    item.present ? 'bg-green-50' : 'bg-gray-50'
                  )}
                >
                  {/* Status icon */}
                  <div className='mt-[2px] flex size-5 shrink-0 items-center justify-center'>
                    {item.present ? (
                      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                        <circle cx='10' cy='10' r='10' fill='#22C55E' />
                        <path
                          d='M6 10.5L8.5 13L14 7.5'
                          stroke='white'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                        <circle cx='10' cy='10' r='9' stroke='#D1D5DB' strokeWidth='2' />
                      </svg>
                    )}
                  </div>

                  {/* Content */}
                  <div className='flex flex-1 flex-col'>
                    <span
                      className={clsx(
                        'font-sans text-14 font-semibold leading-20',
                        item.present ? 'text-dark-300' : 'text-gray-400'
                      )}
                    >
                      {item.label}
                    </span>
                    <span
                      className={clsx(
                        'font-sans text-12 leading-18',
                        item.present ? 'text-gray-530' : 'text-gray-350'
                      )}
                    >
                      {item.detail}
                    </span>
                  </div>

                  {/* Cost badge */}
                  {item.cost && item.present && (
                    <span className='mt-[2px] shrink-0 rounded-full bg-brand-100 px-2 py-0.5 font-sans text-11 font-semibold text-brand-800'>
                      {item.cost} cr
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Tip if low content */}
            {presentCount <= 1 && (
              <div className='mb-5 rounded-12 border border-amber-200 bg-amber-50 px-4 py-3'>
                <p className='font-sans text-13 font-medium leading-18 text-amber-800'>
                  Tip: Adding more training data (files, links, or internet search) will make your
                  echo smarter and more accurate.
                </p>
              </div>
            )}

            {/* Cost summary */}
            <div className='mb-6 flex items-center justify-between border-t border-gray-200 pt-4'>
              <span className='font-sans text-14 font-normal text-gray-530'>Total cost</span>
              <span className='font-sans text-20 font-bold text-dark-300'>
                {totalCost > 0 ? `${totalCost} credits` : 'Free'}
              </span>
            </div>

            <div className='flex flex-col gap-3'>
              <Button
                variant='purple-dark'
                onClick={onConfirm}
                className='h-[48px] w-full text-16 font-bold uppercase tracking-wide'
              >
                START TRAINING
              </Button>
              <button
                onClick={onClose}
                className='font-sans text-14 font-medium text-dark-200 transition-colors hover:text-dark-300'
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className='mb-3 font-sans text-24 font-bold leading-tight text-dark-300'>
              Not Enough Credits
            </h2>
            <p className='mb-5 font-sans text-16 leading-27 text-dark-200'>
              Training costs <span className='font-bold'>{totalCost} credits</span> but you have{' '}
              <span className='font-bold'>{userCredits}</span>. You need{' '}
              <span className='font-bold text-red-500'>{missingCredits} more</span>.
            </p>

            {/* Show what costs credits */}
            <div className='mb-5 flex flex-col gap-2'>
              {checklist
                .filter((item) => item.cost && item.present)
                .map((item) => (
                  <div
                    key={item.label}
                    className='flex items-center justify-between rounded-12 bg-gray-50 px-4 py-3'
                  >
                    <span className='font-sans text-14 font-medium text-dark-200'>
                      {item.label}
                    </span>
                    <span className='font-sans text-14 font-bold text-dark-300'>
                      {item.cost} credits
                    </span>
                  </div>
                ))}
            </div>

            <div className='flex flex-col gap-3'>
              <Button
                variant='purple-dark'
                onClick={handleBuyCredits}
                className='h-[48px] w-full text-16 font-bold uppercase tracking-wide'
              >
                BUY CREDITS
              </Button>
              <button
                onClick={onClose}
                className='font-sans text-14 font-medium text-dark-200 transition-colors hover:text-dark-300'
              >
                Maybe Later
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
